import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ErrorNote } from '@/components/ui';
import { formatDate } from '@/format';
import { REFRESH_MS } from './useOverview';

/** How far ahead the panel looks. */
const DAYS_AHEAD = 14;
const MAX_ROWS = 6;

function isoDay(offsetDays = 0): string {
  const d = new Date(Date.now() + offsetDays * 86_400_000);
  return d.toISOString().slice(0, 10);
}

/**
 * The next holidays and the reminders due this fortnight, in one short list —
 * the two things on the calendar that change what today looks like. Reading
 * the calendar needs no permission, so everyone gets this.
 */
export function ComingUp({ onOpenCalendar }: { onOpenCalendar: () => void }) {
  const from = isoDay();
  const to = isoDay(DAYS_AHEAD);

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['calendar', from, to],
    queryFn: () => api.get('/api/v1/calendar', { from, to }),
    refetchInterval: REFRESH_MS,
  });

  const holidays = (data?.holidays ?? []).map((h) => ({
    key: `h-${h.id}`,
    when: h.startDate,
    icon: 'month' as const,
    title: h.nameEn,
    note: h.status === 'EXPECTED' ? 'Expected date' : 'Holiday',
  }));

  const reminders = (data?.reminders ?? [])
    .filter((r) => !r.completed)
    .map((r) => ({
      key: `r-${r.reminderId}-${r.occurrenceAt}`,
      when: r.occurrenceAt.slice(0, 10),
      icon: 'checklist' as const,
      title: r.title,
      note: r.visibility === 'SHARED' ? 'Shared reminder' : 'Your reminder',
    }));

  const rows = [...holidays, ...reminders]
    .sort((a, b) => a.when.localeCompare(b.when))
    .slice(0, MAX_ROWS);

  return (
    <section className="panel comingup">
      <div className="panel__head">
        <h3 className="panel__title">Coming up</h3>
        <button type="button" className="btn btn--sm btn--ghost" onClick={onOpenCalendar}>
          Calendar
        </button>
      </div>

      {isError && <ErrorNote message={error.message} />}
      {isPending && <p className="muted">Loading…</p>}

      {!isPending && !isError && rows.length === 0 && (
        <p className="muted">Nothing in the next two weeks.</p>
      )}

      {rows.length > 0 && (
        <ul className="stacklist">
          {rows.map((row) => (
            <li key={row.key} className="comingup__row">
              <span className="comingup__icon" aria-hidden="true">
                <AppIcon kind={row.icon} size={15} />
              </span>
              <span className="comingup__what">
                <span className="comingup__title">{row.title}</span>
                <span className="comingup__note">{row.note}</span>
              </span>
              <span className="comingup__when">{formatDate(row.when)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
